/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { CheckCircle2, XCircle } from 'lucide-react';
import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type Proforma } from '../../lib/api/proformas.js';
import { useProformas } from '../../lib/queries/proformas.js';
import { formatIst } from '../../lib/utils/datetime.js';
import { formatInr } from '../../lib/utils/money.js';
import { ApprovedActions } from '../admin/proforma-convert.js';

type Tab = 'all' | 'approved' | 'declined';
const TABS: readonly Tab[] = ['all', 'approved', 'declined'];

function InboxCard({ proforma }: { readonly proforma: Proforma }): JSX.Element {
  const { t } = useTranslation();
  const approved = proforma.status === 'approved';
  const when = proforma.respondedAt ?? proforma.createdAt;
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <span className="mt-0.5 shrink-0">
        {approved ? (
          <CheckCircle2 className="h-5 w-5 text-emerald-500" aria-hidden="true" />
        ) : (
          <XCircle className="h-5 w-5 text-rose-500" aria-hidden="true" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-slate-800 dark:text-white">
          {t(approved ? 'finance.inbox.approvedTitle' : 'finance.inbox.declinedTitle', {
            name: proforma.clientName,
          })}
        </p>
        <p className="truncate text-sm text-slate-500 dark:text-slate-400">
          {proforma.proformaNumber} · {formatInr(proforma.totalPaise)}
        </p>
        {!approved && proforma.declineReason !== null && (
          <p className="mt-1 text-xs text-rose-600 dark:text-rose-300">
            {t('finance.inbox.reason', { reason: proforma.declineReason })}
          </p>
        )}
        <p className="mt-1 text-xs text-slate-400">{formatIst(when, 'd MMM yyyy, h:mm a')}</p>
      </div>
      <div className="shrink-0">
        {approved ? (
          <ApprovedActions proforma={proforma} />
        ) : (
          <span className="text-xs text-slate-400">{t('finance.inbox.followUpRaised')}</span>
        )}
      </div>
    </div>
  );
}

/** Finance → Inbox (Figma): proforma approve/decline action queue. */
export function FinanceInboxPage(): JSX.Element {
  const { t } = useTranslation();
  const proformas = useProformas();
  const [tab, setTab] = useState<Tab>('all');
  const select = useCallback(
    (next: Tab) => () => {
      setTab(next);
    },
    [],
  );
  const actionable = (proformas.data ?? []).filter(
    (p) => p.status === 'approved' || p.status === 'declined',
  );
  const rows = tab === 'all' ? actionable : actionable.filter((p) => p.status === tab);

  return (
    <div className="p-8">
      <PageHeader title={t('finance.inbox.title')} subtitle={t('finance.inbox.subtitle')} />
      <div className="mb-6 inline-flex rounded-xl bg-slate-100 p-1 dark:bg-slate-800">
        {TABS.map((value) => (
          <button
            key={value}
            type="button"
            onClick={select(value)}
            className={`focus-ring rounded-lg px-5 py-2 text-sm font-medium ${
              tab === value ? 'bg-[var(--accent)] text-white' : 'text-slate-600 dark:text-slate-300'
            }`}
          >
            {t(`finance.inbox.tab_${value}`)}
          </button>
        ))}
      </div>
      <QueryState
        isLoading={proformas.isLoading}
        isError={proformas.isError}
        isEmpty={rows.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('finance.inbox.empty')}
      >
        <div className="space-y-3">
          {rows.map((p) => (
            <InboxCard key={p.id} proforma={p} />
          ))}
        </div>
      </QueryState>
    </div>
  );
}
